import React, { memo, useEffect, useState } from 'react';
import { fmtBRL, fmtInt, unidadeLabel } from 'services/price_intelligence';
import {
  formatarQuantidade,
  ItemLote,
  Lote,
  nomeDoLote,
  progressoDoLote,
  totaisDoLote,
  totalEstimado,
} from 'services/price_intelligence_batch';
import { Container } from './styles';

export interface AndamentoLote {
  total: number;
  feitos: number;
  falhas: number;
  atualId: string | null;
}

export interface AvisoLote {
  tom: 'ok' | 'atencao';
  texto: string;
}

const rotuloQuantidade = (item: ItemLote): string => {
  if (item.quantidade === null) return item.unidade || '—';
  return `${formatarQuantidade(item.quantidade)}${item.unidade ? ` ${item.unidade}` : ''}`;
};

interface LinhaProps {
  item: ItemLote;
  indice: number;
  ativo: boolean;
  pesquisando: boolean;
  travado: boolean;
  podeVer: boolean;
  falhou: boolean;
  onPesquisar: (item: ItemLote) => void;
  onVerResultado: (item: ItemLote) => void;
  onRemover: (id: string) => void;
  onAbrirSalva: (id: number) => void;
}

const Linha = memo(
  ({
    item,
    indice,
    ativo,
    pesquisando,
    travado,
    podeVer,
    falhou,
    onPesquisar,
    onVerResultado,
    onRemover,
    onAbrirSalva,
  }: LinhaProps) => {
    const pesquisado = item.status !== 'pendente';
    const { resumo } = item;
    const total = totalEstimado(item);
    const meta = [
      item.lote ? `Lote ${item.lote}` : null,
      item.codigoCatalogo ? `Catálogo ${item.codigoCatalogo}` : null,
      item.valorReferencia !== null ? `Ref. do arquivo ${fmtBRL(item.valorReferencia)}` : null,
    ].filter(Boolean);
    const preco =
      resumo && resumo.valor !== null
        ? `${fmtBRL(resumo.valor)}${resumo.unidade ? `/${unidadeLabel(resumo.unidade)}` : ''}`
        : null;

    let aviso: React.ReactNode = null;
    if (pesquisando) aviso = <p className="row-note info">Pesquisando…</p>;
    else if (falhou)
      aviso = <p className="row-note danger">A pesquisa deste item falhou — tente de novo.</p>;
    else if (resumo && resumo.amostras === 0)
      aviso = (
        <p className="row-note warn">Nenhuma amostra — ajuste a descrição ou amplie o período.</p>
      );
    else if (resumo && resumo.valor === null)
      aviso = (
        <p className="row-note warn">
          {fmtInt(resumo.amostras)} amostras, mas só com valores globais de contrato — sem preço
          unitário de referência.
        </p>
      );
    else if (resumo && resumo.refinar)
      aviso = (
        <p className="row-note warn">Descrição vaga para cotar — vale refinar. {resumo.refinar}</p>
      );

    return (
      <tr className={ativo ? 'active' : ''}>
        <td>
          <span
            className={`row-badge ${pesquisando ? 'running' : ''} ${pesquisado ? 'done' : ''}`}
            title={item.numero ? `Item ${item.numero} do arquivo` : `Item ${indice + 1}`}
          >
            {pesquisando ? (
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            ) : pesquisado ? (
              <i className="material-icons">check</i>
            ) : (
              item.numero || indice + 1
            )}
          </span>
        </td>
        <td className="item-cell">
          <p className="item-desc" title={item.descricao}>
            {item.descricao}
          </p>
          {meta.length > 0 && <p className="item-meta">{meta.join(' · ')}</p>}
          {item.status === 'salvo' && item.pesquisaSalvaId && (
            <button
              type="button"
              className="link-saved"
              onClick={() => item.pesquisaSalvaId && onAbrirSalva(item.pesquisaSalvaId)}
            >
              <i className="material-icons">bookmark</i>
              Pesquisa salva — abrir
            </button>
          )}
          {aviso}
        </td>
        <td className="num">{rotuloQuantidade(item)}</td>
        <td className="num">
          <strong>{preco || '—'}</strong>
          {resumo && (
            <small>
              {fmtInt(resumo.amostras)} {resumo.amostras === 1 ? 'amostra' : 'amostras'}
            </small>
          )}
        </td>
        <td className="num">{total !== null ? fmtBRL(total) : '—'}</td>
        <td className="actions">
          {pesquisado ? (
            <>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={!podeVer}
                onClick={() => onVerResultado(item)}
                title="Abrir o resultado desta pesquisa"
              >
                <i className="material-icons">visibility</i>
                Ver
              </button>
              <button
                type="button"
                className="icon-btn"
                disabled={travado}
                onClick={() => onPesquisar(item)}
                title="Pesquisar de novo"
                aria-label="Pesquisar de novo"
              >
                <i className="material-icons">refresh</i>
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              disabled={travado}
              onClick={() => onPesquisar(item)}
            >
              <i className="material-icons">search</i>
              Pesquisar
            </button>
          )}
          <button
            type="button"
            className="icon-btn"
            disabled={pesquisando}
            onClick={() => onRemover(item.id)}
            title="Remover da lista"
            aria-label="Remover da lista"
          >
            <i className="material-icons">close</i>
          </button>
        </td>
      </tr>
    );
  },
);

interface PriceIntelligenceBatchProps {
  lote: Lote;
  itemAtivoId: string | null;
  ocupado: boolean;
  compacto: boolean;
  andamento: AndamentoLote | null;
  salvando: { feitos: number; total: number } | null;
  falhas: Record<string, boolean>;
  porSalvar: number;
  aviso: AvisoLote | null;
  onFecharAviso: () => void;
  onPesquisar: (item: ItemLote) => void;
  onVerResultado: (item: ItemLote) => void;
  onRemover: (id: string) => void;
  onAbrirSalva: (id: number) => void;
  onLimpar: () => void;
  onImportar: () => void;
  onPesquisarTodos: () => void;
  onParar: () => void;
  onExportar: () => void;
  onSalvarTodas: () => void;
}

const PriceIntelligenceBatch = ({
  lote,
  itemAtivoId,
  ocupado,
  compacto,
  andamento,
  salvando,
  falhas,
  porSalvar,
  aviso,
  onFecharAviso,
  onPesquisar,
  onVerResultado,
  onRemover,
  onAbrirSalva,
  onLimpar,
  onImportar,
  onPesquisarTodos,
  onParar,
  onExportar,
  onSalvarTodas,
}: PriceIntelligenceBatchProps) => {
  const [expandida, setExpandida] = useState<boolean>(!compacto);

  useEffect(() => setExpandida(!compacto), [compacto]);

  const progresso = progressoDoLote(lote);
  const totais = totaisDoLote(lote);
  const ativo = itemAtivoId ? lote.itens.find((i) => i.id === itemAtivoId) || null : null;
  const posicaoAtivo = ativo ? lote.itens.indexOf(ativo) + 1 : 0;
  const pct = progresso.total ? Math.round((progresso.pesquisados / progresso.total) * 100) : 0;
  const itemDaFila =
    andamento && andamento.atualId
      ? lote.itens.find((i) => i.id === andamento.atualId) || null
      : null;
  const travado = ocupado || Boolean(andamento) || Boolean(salvando);

  const pesquisar = (item: ItemLote) => {
    setExpandida(false);
    onPesquisar(item);
  };

  const verResultado = (item: ItemLote) => {
    setExpandida(false);
    onVerResultado(item);
  };

  return (
    <Container>
      <div className="batch-head">
        <button
          type="button"
          className="batch-toggle"
          onClick={() => setExpandida((v) => !v)}
          aria-expanded={expandida}
        >
          <span className="batch-icon">
            <i className="material-icons">list_alt</i>
          </span>
          <span className="batch-title-wrap">
            <span className="batch-title">
              Itens do arquivo
              <i className={`material-icons ${expandida ? 'open' : ''}`}>expand_more</i>
            </span>
            <span className="batch-sub">
              {!expandida && ativo && !andamento
                ? `Item ${posicaoAtivo} de ${progresso.total}: ${ativo.descricao}`
                : `${nomeDoLote(lote)} · ${fmtInt(progresso.pesquisados)} de ${fmtInt(
                    progresso.total,
                  )} pesquisados${progresso.salvos ? ` · ${fmtInt(progresso.salvos)} salvos` : ''}`}
            </span>
          </span>
        </button>

        <div className="batch-actions">
          {andamento ? (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onParar}>
              <i className="material-icons">stop</i>
              Parar
            </button>
          ) : progresso.pendentes > 0 ? (
            <button
              type="button"
              className="btn btn-primary btn-ia btn-sm"
              disabled={travado}
              onClick={onPesquisarTodos}
              title="Pesquisa os itens pendentes em sequência, um por vez"
            >
              <i className="material-icons">playlist_play</i>
              {progresso.pesquisados === 0
                ? `Pesquisar todos (${fmtInt(progresso.pendentes)})`
                : `Pesquisar os ${fmtInt(progresso.pendentes)} pendentes`}
            </button>
          ) : (
            <span className="all-done">
              <i className="material-icons">check</i>
              Todos pesquisados
            </span>
          )}
          <button
            type="button"
            className="btn btn-link btn-sm"
            disabled={Boolean(andamento) || Boolean(salvando)}
            onClick={onImportar}
          >
            <i className="material-icons">upload_file</i>
            Importar
          </button>
          <button
            type="button"
            className="btn btn-link btn-sm"
            disabled={Boolean(andamento) || Boolean(salvando)}
            onClick={onLimpar}
          >
            <i className="material-icons">delete_outline</i>
            Limpar
          </button>
        </div>
      </div>

      <div className="batch-progress" role="progressbar" aria-valuenow={pct}>
        <div style={{ width: `${pct}%` }} />
      </div>

      {andamento && (
        <div className="batch-running" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          <span className="running-text">
            <strong>
              Pesquisando {Math.min(andamento.feitos + andamento.falhas + 1, andamento.total)} de{' '}
              {andamento.total}
            </strong>
            {itemDaFila ? `: ${itemDaFila.descricao}` : ''}
          </span>
          <span className="running-hint">
            {andamento.falhas ? `${andamento.falhas} com falha · ` : ''}um item por vez · os prontos
            já podem ser abertos
          </span>
        </div>
      )}

      {aviso && (
        <div
          className={`alert ${aviso.tom === 'ok' ? 'alert-success' : 'alert-warning'} batch-alert`}
        >
          <span>{aviso.texto}</span>
          <button type="button" className="close" aria-label="Fechar aviso" onClick={onFecharAviso}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
      )}

      {progresso.pesquisados > 0 && (
        <div className="batch-totals">
          <p>
            {totais.itensNoTotal > 0 ? (
              <>
                Total estimado <strong>{fmtBRL(totais.total)}</strong>{' '}
                <span>
                  ({fmtInt(totais.itensNoTotal)} de {fmtInt(progresso.total)} itens · quantidade ×
                  mediana)
                </span>
              </>
            ) : (
              <span>Sem total estimado: faltam quantidades ou preços de referência.</span>
            )}
          </p>
          <div className="totals-actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onExportar}
              title="Baixa a tabela-resumo em CSV (abre no Excel)"
            >
              <i className="material-icons">download</i>
              Exportar tabela
            </button>
            {porSalvar === 0 && !salvando ? (
              <a href="/pesquisa_de_precos/salvas" className="btn btn-outline-secondary btn-sm">
                <i className="material-icons">bookmark</i>
                Ver pesquisas salvas
              </a>
            ) : (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={Boolean(andamento) || Boolean(salvando) || ocupado}
                onClick={onSalvarTodas}
                title="Salva uma pesquisa por item, agrupadas no lote deste arquivo"
              >
                {salvando ? (
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                ) : (
                  <i className="material-icons">bookmark_border</i>
                )}
                {salvando
                  ? `Salvando ${Math.min(salvando.feitos + 1, salvando.total)} de ${salvando.total}`
                  : `Salvar ${fmtInt(porSalvar)} ${porSalvar === 1 ? 'pesquisa' : 'pesquisas'}`}
              </button>
            )}
          </div>
        </div>
      )}

      {expandida && (
        <div className="batch-table">
          <table className="table mb-0">
            <thead>
              <tr>
                <th style={{ width: 44 }} aria-label="Situação" />
                <th>Item</th>
                <th className="num">Quantidade</th>
                <th className="num">Preço de ref.</th>
                <th className="num">Total estimado</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {lote.itens.map((item, indice) => {
                const ehAtivo = item.id === itemAtivoId;
                return (
                  <Linha
                    key={item.id}
                    item={item}
                    indice={indice}
                    ativo={ehAtivo}
                    pesquisando={andamento ? andamento.atualId === item.id : ehAtivo && ocupado}
                    travado={travado}
                    podeVer={!ocupado && !salvando}
                    falhou={Boolean(falhas[item.id])}
                    onPesquisar={pesquisar}
                    onVerResultado={verResultado}
                    onRemover={onRemover}
                    onAbrirSalva={onAbrirSalva}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Container>
  );
};

export default PriceIntelligenceBatch;
